import type { ActionReducerMapBuilder } from "@reduxjs/toolkit";
import type { DatabaseState } from "./types";
import {
  createStoreAction,
  createStoreFileAction,
  deleteStoreAction,
  deleteStoreFileAction,
  getStoreFilesAction,
  getStoresAction,
  updateStoreAction,
} from "./actions/openai_store_actions";
import {
  createLocalStoreAction,
  createLocalStoreFileAction,
  deleteLocalStoreAction,
  deleteLocalStoreFileAction,
  getLocalStoreFilesAction,
  getLocalStoresAction,
  updateLocalStoreAction,
} from "./actions/local_store_actions";
import { updateStoreFiles } from "../helpers";

export function getStoresReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(getStoresAction.pending, (state) => {
    state.isStoreLoading = true;
  });
  builder.addCase(getStoresAction.fulfilled, (state, action) => {
    const { finalStores, files } = action.payload;
    state.dataStore = finalStores;
    state.isStoreLoading = false;
    state.hasMutation = false;
    if (files) {
      state.fileList = files;
    }
  });
  builder.addCase(getStoresAction.rejected, (state) => {
    state.isStoreLoading = false;
  });
}

export function getLocalStoresReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(getLocalStoresAction.pending, (state) => {
    state.isStoreLoading = true;
  });
  builder.addCase(getLocalStoresAction.fulfilled, (state, action) => {
    const stores = action.payload;
    state.dataStore = stores;
    state.isStoreLoading = false;
    state.hasMutation = false;
  });
  builder.addCase(getLocalStoresAction.rejected, (state) => {
    state.isStoreLoading = false;
  });
}

export function getStoreFilesReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(getStoreFilesAction.pending, (state) => {
    state.isFileLoading = true;
  });
  builder.addCase(getStoreFilesAction.fulfilled, (state, action) => {
    if (!action.payload) return;
    const updates = action.payload;
    state.dataStore = updates.newStores;
    state.selectedStore = updates.newStore;
    state.isFileLoading = false;
  });
  builder.addCase(getStoreFilesAction.rejected, (state) => {
    state.isFileLoading = false;
  });
}

export function getLocalStoreFilesReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(getLocalStoreFilesAction.pending, (state) => {
    state.isFileLoading = true;
  });
  builder.addCase(getLocalStoreFilesAction.fulfilled, (state, action) => {
    if (!action.payload) return;
    const updates = action.payload;
    state.dataStore = updates.newStores;
    state.selectedStore = updates.newStore;
    state.isFileLoading = false;
  });
  builder.addCase(getLocalStoreFilesAction.rejected, (state) => {
    state.isFileLoading = false;
  });
}

export function createStoreReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(createStoreAction.pending, (state) => {
    state.isStoreLoading = true;
  });
  builder.addCase(createStoreAction.fulfilled, (state, action) => {
    state.dataStore = [action.payload, ...state.dataStore];
    state.selectedStore = action.payload;
    state.isStoreLoading = false;
    state.hasMutation = true;
  });
  builder.addCase(createStoreAction.rejected, (state) => {
    state.isStoreLoading = false;
  });
}

export function createLocalStoreReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(createLocalStoreAction.pending, (state) => {
    state.isStoreLoading = true;
  });
  builder.addCase(createLocalStoreAction.fulfilled, (state, action) => {
    state.dataStore = [...state.dataStore, action.payload];
    state.selectedStore = action.payload;
    state.isStoreLoading = false;
    state.hasMutation = true;
  });
  builder.addCase(createLocalStoreAction.rejected, (state) => {
    state.isStoreLoading = false;
  });
}

export function createStoreFileReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(createStoreFileAction.fulfilled, (state, action) => {
    const { tempFileId, finalFile, storeId } = action.payload;
    updateStoreFiles(state, storeId, tempFileId, finalFile);
  });
  builder.addCase(createStoreFileAction.rejected, (state, action) => {
    const { tempFileId, storeId } = action.meta.arg;
    state.dataStore = state.dataStore.map((store) => {
      if (store.id !== storeId) return store;

      const fileIndex = store.files.findIndex((file) => file.id === tempFileId);
      if (fileIndex === -1) return store;

      const updatedFiles = [...store.files];
      updatedFiles[fileIndex] = {
        ...updatedFiles[fileIndex],
        isLoading: false,
      };

      return {
        ...store,
        files: updatedFiles,
      };
    });

    if (state.selectedStore && state.selectedStore.id === storeId) {
      const fileIndex = state.selectedStore.files.findIndex(
        (file) => file.id === tempFileId,
      );
      if (fileIndex !== -1) {
        const updatedFiles = [...state.selectedStore.files];
        updatedFiles[fileIndex] = {
          ...updatedFiles[fileIndex],
          isLoading: false,
        };
        state.selectedStore = {
          ...state.selectedStore,
          files: updatedFiles,
        };
      }
    }
  });
}

export function createLocalStoreFileReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(createLocalStoreFileAction.fulfilled, (state, action) => {
    const { tempFileId, finalFile, storeId } = action.payload;
    updateStoreFiles(state, storeId, tempFileId, finalFile);
  });
  builder.addCase(createLocalStoreFileAction.rejected, (state, action) => {
    const { tempFileId, storeId } = action.meta.arg;
    state.dataStore = state.dataStore.map((store) => {
      if (store.id !== storeId) return store;

      return {
        ...store,
        files: store.files.filter((file) => file.id !== tempFileId),
      };
    });

    if (state.selectedStore && state.selectedStore.id === storeId) {
      state.selectedStore = {
        ...state.selectedStore,
        files: state.selectedStore.files.filter(
          (file) => file.id !== tempFileId,
        ),
      };
    }
  });
}

export function updateStoreReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(updateStoreAction.pending, (state) => {
    state.isStoreLoading = true;
  });
  builder.addCase(updateStoreAction.fulfilled, (state, action) => {
    state.isStoreLoading = false;
    state.hasMutation = true;
    if (action.payload) {
      state.dataStore = action.payload.newStores;
      state.selectedStore = action.payload.newStore;
    }
  });
  builder.addCase(updateStoreAction.rejected, (state) => {
    state.isStoreLoading = false;
  });
}

export function updateLocalStoreReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(updateLocalStoreAction.pending, (state) => {
    state.isStoreLoading = true;
  });
  builder.addCase(updateLocalStoreAction.fulfilled, (state, action) => {
    state.isStoreLoading = false;
    state.hasMutation = true;
    if (action.payload) {
      state.dataStore = action.payload.newStores;
      state.selectedStore = action.payload.newStore;
    }
  });
  builder.addCase(updateLocalStoreAction.rejected, (state) => {
    state.isStoreLoading = false;
  });
}

export function deleteStoreReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(deleteStoreAction.pending, (state) => {
    state.isStoreLoading = true;
  });
  builder.addCase(deleteStoreAction.fulfilled, (state) => {
    state.isStoreLoading = false;
    state.hasMutation = true;
  });
  builder.addCase(deleteStoreAction.rejected, (state) => {
    state.isStoreLoading = false;
  });
}

export function deleteLocalStoreReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(deleteLocalStoreAction.pending, (state) => {
    state.isStoreLoading = true;
  });
  builder.addCase(deleteLocalStoreAction.fulfilled, (state) => {
    state.isStoreLoading = false;
    state.hasMutation = true;
  });
  builder.addCase(deleteLocalStoreAction.rejected, (state) => {
    state.isStoreLoading = false;
  });
}

export function deleteFileReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(deleteStoreFileAction.pending, (state) => {
    state.isFileLoading = true;
  });
  builder.addCase(deleteStoreFileAction.fulfilled, (state, action) => {
    if (!action.payload) return;
    const { newFileList, updates } = action.payload;
    state.hasMutation = true;
    state.dataStore = updates.newStores;
    state.selectedStore = updates.newStore;
    state.fileList = newFileList;
    state.isFileLoading = false;
  });
  builder.addCase(deleteStoreFileAction.rejected, (state) => {
    state.isFileLoading = false;
  });
}

export function deleteLocalFileReducerBuilder(
  builder: ActionReducerMapBuilder<DatabaseState>,
) {
  builder.addCase(deleteLocalStoreFileAction.pending, (state) => {
    state.isFileLoading = true;
  });
  builder.addCase(deleteLocalStoreFileAction.fulfilled, (state, action) => {
    if (!action.payload) return;
    const updates = action.payload;
    state.hasMutation = true;
    state.dataStore = updates.newStores;
    state.selectedStore = updates.newStore;
    state.isFileLoading = false;
  });
  builder.addCase(deleteLocalStoreFileAction.rejected, (state) => {
    state.isFileLoading = false;
  });
}
